/**
 * Utilities for tracking new artifacts using sessionStorage.
 * These are pure functions that can be called from anywhere in the app.
 *
 * Two tracking levels:
 * 1. Tab badge - Shows count on Files tab, clears when tab is viewed
 * 2. Row pills - Shows NEW! on file rows, clears when downloaded or page unmounted
 */

const getTabBadgeKey = (systemId: string) => `system-${systemId}-new-artifacts-badge`;
const getRowPillsKey = (systemId: string) => `system-${systemId}-new-artifacts-pills`;

/**
 * Add a new artifact ID to both tracking sets
 */
export function addNewArtifact(systemId: string, artifactId: string): void {
  try {
    // Add to tab badge tracking
    const badgeKey = getTabBadgeKey(systemId);
    const badgeStored = sessionStorage.getItem(badgeKey);
    const badgeArtifacts = badgeStored ? (JSON.parse(badgeStored) as string[]) : [];
    if (!badgeArtifacts.includes(artifactId)) {
      badgeArtifacts.push(artifactId);
      sessionStorage.setItem(badgeKey, JSON.stringify(badgeArtifacts));
    }

    // Add to row pills tracking
    const pillsKey = getRowPillsKey(systemId);
    const pillsStored = sessionStorage.getItem(pillsKey);
    const pillsArtifacts = pillsStored ? (JSON.parse(pillsStored) as string[]) : [];
    if (!pillsArtifacts.includes(artifactId)) {
      pillsArtifacts.push(artifactId);
      sessionStorage.setItem(pillsKey, JSON.stringify(pillsArtifacts));
    }
  } catch (error) {
    console.error('Failed to add new artifact to sessionStorage:', error);
  }
}

/**
 * Get tracked artifact IDs for tab badge (cleared when Files tab viewed)
 */
export function getNewArtifactsForBadge(systemId: string): string[] {
  try {
    const key = getTabBadgeKey(systemId);
    const stored = sessionStorage.getItem(key);
    return stored ? (JSON.parse(stored) as string[]) : [];
  } catch (error) {
    console.error('Failed to get badge artifacts from sessionStorage:', error);
    return [];
  }
}

/**
 * Get tracked artifact IDs for row pills (cleared when downloaded or page unmounted)
 */
export function getNewArtifactsForPills(systemId: string): string[] {
  try {
    const key = getRowPillsKey(systemId);
    const stored = sessionStorage.getItem(key);
    return stored ? (JSON.parse(stored) as string[]) : [];
  } catch (error) {
    console.error('Failed to get pill artifacts from sessionStorage:', error);
    return [];
  }
}

/**
 * Clear tab badge tracking (called when Files tab is viewed)
 */
export function clearBadgeArtifacts(systemId: string): void {
  try {
    const key = getTabBadgeKey(systemId);
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear badge artifacts from sessionStorage:', error);
  }
}

/**
 * Clear row pill tracking (called on page unmount)
 */
export function clearPillArtifacts(systemId: string): void {
  try {
    const key = getRowPillsKey(systemId);
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear pill artifacts from sessionStorage:', error);
  }
}

/**
 * Remove a specific artifact from pill tracking (called when downloaded)
 */
export function removeArtifactFromPills(systemId: string, artifactId: string): void {
  try {
    const key = getRowPillsKey(systemId);
    const stored = sessionStorage.getItem(key);
    if (stored) {
      const artifacts = JSON.parse(stored) as string[];
      const updated = artifacts.filter(id => id !== artifactId);
      if (updated.length > 0) {
        sessionStorage.setItem(key, JSON.stringify(updated));
      } else {
        sessionStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error('Failed to remove artifact from pills:', error);
  }
}
